"use client";

/**
 * ToolCard - Base container for tool displays
 *
 * Provides consistent styling and status-based visual feedback
 * for all tool display components.
 */

import type { ReactNode } from "react";
import { Card } from "@/ui";
import { colors, radius, shadows } from "@/ui/tokens";
import { cn } from "@/lib/utils";
import type { ToolStatus } from "@/lib/sola/types/tools";
import styles from "./ToolCard.module.css";

export interface ToolCardProps {
  status: ToolStatus;
  children: ReactNode;
  className?: string;
  noPadding?: boolean;
}

/**
 * Get border color based on tool status
 */
function getStatusBorderColor(status: ToolStatus): string {
  switch (status) {
    case 'complete':
      return colors.signal.success;
    case 'error':
      return colors.signal.danger;
    case 'executing':
    case 'streaming':
      return colors.signal.warning;
    case 'input-streaming':
    case 'input-ready':
      return colors.action.product;
    default:
      return colors.border.subtle;
  }
}

/**
 * Get subtle background color based on status
 */
function getStatusBackgroundColor(status: ToolStatus): string {
  switch (status) {
    case 'error':
      return colors.signal.dangerLight;
    case 'executing':
    case 'streaming':
      return colors.signal.warningLight;
    default:
      return colors.canvas.default;
  }
}

export function ToolCard({ status, children, className, noPadding = false }: ToolCardProps) {
  const isExecuting = status === 'executing' || status === 'streaming';

  return (
    <Card
      variant="outlined"
      padding="none"
      className={cn("w-full overflow-hidden", isExecuting && styles.executingPulse, className)}
      style={{
        borderColor: getStatusBorderColor(status),
        backgroundColor: getStatusBackgroundColor(status),
        borderRadius: radius.lg,
        boxShadow: shadows[2],
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
      }}
    >
      {children}
    </Card>
  );
}

export default ToolCard;
